import { ArrowsRightLeftIcon, QrCodeIcon } from '@heroicons/react/24/solid'

import { anim } from '../../motion'
import { COLORS } from '../../primitives'
import { Flight, Person } from '../../v3/figures'
import { U, iso, onWallA } from '../../v3/iso'
import { Anchor, Caption, Tag } from '../kit'
import { cam, cameraAt } from '../timeline'

/**
 * Extra „Talentpool“: Nach den Absagen bleibt der Pool nicht leer. Über den
 * öffentlichen Einladungslink kommen neue Talente dazu, ganz ohne konkrete
 * Bewerbung. Bei einer neuen Stelle hilft das Kreuzmatching.
 */

const LOOKS = [
  { body: '#F2B48F', skin: '#C68B59', hair: '#2B1D14' },
  { body: '#7FD3E6', skin: '#8D5524', hair: '#182033' },
  { body: '#C9A7F2', skin: '#F1D3B3', hair: '#E6C27A' },
  { body: '#9FE0C2', skin: '#E0AC69', hair: '#5A3825' },
  { body: 'pink', skin: '#F2C9A5', hair: '#7A4A2A' },
  { body: '#4461D3', skin: '#F1D3B3', hair: '#2B1D14' },
  { body: 'yellow', skin: '#8D5524', hair: '#182033' },
  { body: '#B2B4B6', skin: '#E0AC69', hair: '#2B1D14' },
  { body: 'green', skin: '#F2C9A5', hair: '#5A3825' },
  { body: '#F7C6D9', skin: '#C68B59', hair: '#7A4A2A' },
  { body: '#8FA4D8', skin: '#F1D3B3', hair: '#B2B4B6' },
  { body: '#A0B4C8', skin: '#8D5524', hair: '#182033' }
]

const POOL = LOOKS.map((look, i) => {
  const c = i % 4
  const r = Math.floor(i / 4)
  const arrive = 0.24 + i * 0.022
  return {
    look,
    x: 9.4 + c * 1.8 + (r % 2) * 0.6,
    y: 15.4 + r * 2.2,
    arrive,
    match: { 1: 93, 6: 88, 10: 84 }[i]
  }
})

const DOOR = iso(22.2, 0.3, 2.2)
const TOKENS = POOL.map((p, i) => {
  const from = iso(18 + (i % 5) * 2.4, -7, 9 + (i % 3))
  const land = iso(p.x, p.y, 1.4)
  return {
    legs: [
      {
        from,
        to: DOOR,
        at: [p.arrive - 0.1, p.arrive - 0.05],
        arc: 40
      },
      { from: DOOR, to: land, at: [p.arrive - 0.05, p.arrive], arc: 90 }
    ]
  }
})

function LinkToken() {
  return (
    <g>
      <circle r='11' fill={COLORS.blue} stroke='#FFFFFF' strokeWidth='2.5' />
      <path
        d='M-4 2.5 L-1.5 5 Q0 6.5 1.5 5 L5 1.5 Q6.5 0 5 -1.5 L3.5 -3 M4 -2.5 L1.5 -5 Q0 -6.5 -1.5 -5 L-5 -1.5 Q-6.5 0 -5 1.5 L-3.5 3 M-2 2 L2 -2'
        fill='none'
        stroke='#FFFFFF'
        strokeWidth='1.8'
        strokeLinecap='round'
      />
    </g>
  )
}

export function LinkWorld() {
  return (
    <>
      <svg className='off-svg kx-svg' aria-hidden='true'>
        {POOL.map((p, i) => {
          const s = iso(p.x, p.y)
          return (
            <g key={i} transform={`translate(${s.x} ${s.y})`}>
              <g
                className='off-a'
                style={anim({
                  at: [p.arrive, p.arrive + 0.012],
                  out: [0.9, 0.95],
                  s: 0.3,
                  y: 0,
                  oy: 0
                })}
              >
                {p.match && (
                  <g
                    className='off-a'
                    style={anim({ at: [0.64, 0.68], s: 0.2, y: 0 })}
                  >
                    <ellipse
                      rx='20'
                      ry='8.5'
                      fill='none'
                      stroke={COLORS.green}
                      strokeWidth='4'
                    />
                  </g>
                )}
                <Person
                  {...p.look}
                  happy={[p.arrive + 0.02, p.arrive + 0.05]}
                />
                <g transform='translate(-9 -100)'>
                  <g
                    className='off-a'
                    style={anim({
                      at: [p.arrive + 0.03, p.arrive + 0.05],
                      s: 0.3,
                      y: 8
                    })}
                  >
                    <rect
                      width='18'
                      height='22'
                      rx='3'
                      fill='#FFFFFF'
                      stroke={COLORS.pink}
                      strokeWidth='2'
                    />
                    <circle cx='9' cy='8' r='3.2' fill={COLORS.pink} />
                    <rect
                      x='4'
                      y='14'
                      width='10'
                      height='2.5'
                      rx='1.2'
                      fill='#C9D2E6'
                    />
                  </g>
                </g>
              </g>
            </g>
          )
        })}
        <Flight items={TOKENS} render={() => <LinkToken />} />
      </svg>

      <div className='off-overlay'>
        <div
          className='kx-onwall'
          style={{ width: 3.8 * U, transform: onWallA(iso(16.4, 0, 5.4)) }}
        >
          <div
            className='kx-poster'
            style={anim({ at: [0.04, 0.1], out: [0.92, 0.97], y: 0, oy: 0 })}
          >
            <small>Öffentlicher Einladungslink</small>
            <b>Entdecke deine Stärken</b>
            <span>
              <QrCodeIcon width={34} height={34} />
              <i>Talentpool</i>
            </span>
          </div>
        </div>

        <Tag
          at3={[11.6, 21.6, 0]}
          dy={6}
          show={[0.18, 0.24]}
          hide={[0.92, 0.97]}
          tone='yellow'
        >
          Talentpool · Ohne Stellenbezug
        </Tag>

        <Anchor at3={[14.6, 13, 4.4]}>
          <div
            className='kx-newjob'
            style={anim({ at: [0.56, 0.62], out: [0.9, 0.95], y: 16, s: 0.7 })}
          >
            <small>Neue Stelle</small>
            <b>Teamleitung Support</b>
            <span>
              <ArrowsRightLeftIcon width={13} height={13} /> Kreuzmatching
            </span>
          </div>
        </Anchor>

        {POOL.filter((p) => p.match).map((p) => (
          <Anchor key={p.x + p.y} at3={[p.x, p.y, 0]} dy={128}>
            <div
              className='kx-pct'
              style={anim({ at: [0.66, 0.7], out: [0.9, 0.95], y: 8, s: 0.6 })}
            >
              {p.match}&nbsp;%
            </div>
          </Anchor>
        ))}
      </div>
    </>
  )
}

export function LinkScreen() {
  return (
    <>
      <Caption
        show={[0.03, 0.08]}
        hide={[0.24, 0.28]}
        label='Talentpool'
        title='Der Talentpool wächst weiter.'
        short='Wer nicht genommen wurde, bleibt. Neue kommen über den öffentlichen Einladungslink.'
      >
        Wer nicht genommen wurde, bleibt im Talentpool. Und der füllt sich
        weiter, über den öffentlichen Einladungslink.
      </Caption>
      <Caption
        show={[0.28, 0.34]}
        hide={[0.52, 0.56]}
        label='Talentpool'
        title='Ein Link für alle Kanäle.'
        short='Stellenanzeige, Social Media, Karriereseite oder Plakat. Mit Stärkenprofil direkt in den Pool.'
      >
        Link und QR-Code nutzt du in Stellenanzeigen, auf Social Media, der
        Karriereseite oder auf Plakaten. Wer ihn öffnet, macht das Assessment
        und landet mit Stärkenprofil im Pool.
      </Caption>
      <Caption
        show={[0.56, 0.62]}
        hide={[0.92, 0.97]}
        label='Talentpool'
        title='Neue Stelle? Talente sind schon da.'
        short='Per Kreuzmatching findest du sie, bevor die Anzeige online ist.'
      >
        Steht eine neue Stelle an, matchst du den Pool per Kreuzmatching. Die
        passenden Talente sind schon da, bevor die Anzeige online ist.
      </Caption>
    </>
  )
}

export const LINK_EXTRA = {
  id: 'link',
  after: 'absagen',
  len: 400,
  label: 'Talentpool',
  title: 'Ein Link, der den Talentpool füllt',
  teaser:
    'Über den öffentlichen Einladungslink kommen Talente ohne konkrete Bewerbung. Bei einer neuen Stelle sind sie schon da.',
  camera: (p, base) =>
    cameraAt(
      [
        { p: 0, cam: base },
        { p: 0.08, cam: cam([15, 9, 2], 0.95) },
        { p: 0.92, cam: cam([15, 9, 2], 0.95) },
        { p: 1, cam: base }
      ],
      p
    ),
  World: LinkWorld,
  Screen: LinkScreen
}
